import { BigNumber } from '@frontend/numeric';
import { IApiError } from '@frontend/request';

import { EMainProductCodeType, ETenantId } from '@aqx-core/common/api/config';
import { ESymbolCategory, ISymbolDetail } from '@aqx-core/common/api/market';
import { EFillPolicy, EOrderExpiry, EOrderIndicate, EOrderType, IOrder } from '@aqx-core/common/api/order';
import { IUpdateMarketOrderParams, IUpdatePendingOrderParams, tradeAPIController } from '@aqx-core/common/api/trade';
import { EColumnPreferenceOrderType } from '@aqx-core/common/api/user-account-setting';
import { getSecondsSinceEpoch, getUTCOffsetDateTimeAsDayjs } from '@aqx-core/common/dayjs';
import { i18n } from '@aqx-core/common/i18n';
import { mapResponseCodeToMessage } from '@aqx-core/common/request/message';
import { copyTradeAssetViewController } from '@aqx-core/common/services/copy-trade/asset';
import { openOrderViewController } from '@aqx-core/common/services/trade/order/open-order/controller';
import { numberOrDefault } from '@aqx-core/common/utils/number';
import { isMarketOrder } from '@aqx-core/common/utils/order';
import { convertVolumeToUnits } from '@aqx-core/common/utils/price/convertVolumeToUnits';
import { mapOrderTypeLabelAndDirectionToOrderType } from '@aqx-core/common/utils/trade-form';

import { companyService } from '../../company';
import { companyViewController } from '../../company/controller';
import { mixpanelAnalysisService } from '../../mixpanel';
import { EMixpanelPositionModuleTab } from '../../mixpanel/types';
import { orderListingService } from '../../order';
import { orderListingViewController } from '../../order/controller';
import { tradeAnalysisViewController } from '../analysis/controller';
import { equityChartViewController } from '../analysis/equity-chart/controller';
import {
  CASH_FILL_POLICY_DROPDOWN_LIST_CENTROID,
  CASH_FILL_POLICY_DROPDOWN_LIST_HORIZON,
  CASH_FILL_POLICY_DROPDOWN_LIST_LIMIT,
  CASH_FILL_POLICY_DROPDOWN_LIST_MARKET,
  TRADE_EXPIRY_DROPDOWN_LIST,
  TRADE_EXPIRY_DROPDOWN_LIST_MT5,
  TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_DEFAULT,
  TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_HORIZON,
  TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_MT5,
  derivativeFillPolicyDropDownList
} from './constants';
import { calculatePointsFromPrice, calculatePriceFromPoints } from './price-points';
import { tradeFormService } from './service';
import {
  EUIBuySellType,
  EUIOrderTypeLabel,
  IBulkClosePendingOrderFunctionParams,
  ICreateOrderActionParams,
  IEditMarketOrderParams,
  IEditOrderParams,
  IOneClickCloseOrderActionParams
} from './types';
import { checkIsDerivativeMarketOrStopOrder } from './utils';

class TradeFormViewController {
  private orderType?: EOrderType;
  buySellType?: EUIBuySellType;
  private orderTypeLabel?: EUIOrderTypeLabel;
  private signalCopyPending = false;
  private pendingSignalData: {
    entryPrice?: string;
    stopLoss?: string;
    takeProfit?: string;
    buySellType?: EUIBuySellType;
  } | null = null;

  public setSignalCopyPending = (pending: boolean) => {
    this.signalCopyPending = pending;
  };

  /**
   * Returns true if a signal copy is pending and clears the flag.
   * Used by TradeFormTabs to determine the initial tab when OCT is enabled.
   */
  public consumeSignalCopyPending = (): boolean => {
    if (this.signalCopyPending) {
      this.signalCopyPending = false;
      return true;
    }
    return false;
  };

  public setPendingSignalData = (data: {
    entryPrice?: string;
    stopLoss?: string;
    takeProfit?: string;
    buySellType?: EUIBuySellType;
  }) => {
    this.pendingSignalData = data;
  };

  /**
   * Returns the pending signal data and clears it.
   * Used by trade form context to apply signal data after the form is fully mounted.
   */
  public consumePendingSignalData = () => {
    const data = this.pendingSignalData;
    this.pendingSignalData = null;
    return data;
  };

  public setBuySellType = (buySellType?: EUIBuySellType) => {
    this.buySellType = buySellType;
    tradeFormService.setBuySellType(buySellType);
    this.refreshOrderType();
  };

  public getOrderTypeLabel = (): EUIOrderTypeLabel | undefined => {
    return this.orderTypeLabel;
  };

  public getBuySellType = (): EUIBuySellType | undefined => {
    return this.buySellType;
  };

  public setOrderTypeLabel = (orderTypeLabel?: EUIOrderTypeLabel) => {
    this.orderTypeLabel = orderTypeLabel;
    tradeFormService.setOrderTypeLabel(orderTypeLabel);
    this.refreshOrderType();
  };

  public setOrderType = (orderType?: EOrderType) => {
    this.orderType = orderType;
    tradeFormService.setOrderType(orderType);
  };

  public getOrderType = (): EOrderType | undefined => {
    return this.orderType;
  };

  public refreshOrderType = () => {
    if (this.orderTypeLabel) {
      let orderType = this.orderType;
      orderType = mapOrderTypeLabelAndDirectionToOrderType(this.orderTypeLabel, this.buySellType);
      this.setOrderType(orderType);
    }
  };

  public setOrderTypeFromScratch = (orderTypeLabel?: EUIOrderTypeLabel, buySellType?: EUIBuySellType) => {
    // Set order type label
    this.orderTypeLabel = orderTypeLabel;
    tradeFormService.setOrderTypeLabel(orderTypeLabel);

    // Set buy sell type
    this.buySellType = buySellType;
    tradeFormService.setBuySellType(buySellType);

    if (orderTypeLabel && typeof buySellType !== 'undefined') {
      this.setOrderType(mapOrderTypeLabelAndDirectionToOrderType(orderTypeLabel, buySellType));
    }
  };

  public getFillPoliciesFromOrderTypeLabel = (params: {
    orderTypeLabel?: EUIOrderTypeLabel;
    isEdit?: boolean;
    overrideTradeFillPolicy?: ISymbolDetail['tradeFillPolicy'];
  }) => {
    const { orderTypeLabel, isEdit, overrideTradeFillPolicy } = params;

    let tradeFillPolicy = undefined;
    if (isEdit) {
      tradeFillPolicy =
        overrideTradeFillPolicy ?? tradeAnalysisViewController.getEditingSymbolDetail()?.tradeFillPolicy;
    } else {
      tradeFillPolicy = overrideTradeFillPolicy ?? tradeAnalysisViewController.getSymbolDetail()?.tradeFillPolicy;
    }
    const finalTypeLabel = orderTypeLabel || this.orderTypeLabel;

    if (!tradeFillPolicy) {
      return [];
    }
    let fillPolicies;
    switch (finalTypeLabel) {
      case EUIOrderTypeLabel.MARKET:
        fillPolicies = tradeFillPolicy.marketFillPolicy;
        break;
      case EUIOrderTypeLabel.LIMIT:
        fillPolicies = tradeFillPolicy.limitFillPolicy;
        break;
      case EUIOrderTypeLabel.STOP:
        fillPolicies = tradeFillPolicy.stopFillPolicy;
        break;
      case EUIOrderTypeLabel.STOP_LIMIT:
        fillPolicies = tradeFillPolicy.stopLimitFillPolicy;
        break;
    }
    return fillPolicies || [];
  };

  public setOrderTypeLabelFromOrderType = (orderType: EOrderType) => {
    this.setOrderTypeLabel(this.mapOrderTypeLabelFromOrderType(orderType));
  };

  public mapOrderTypeLabelFromOrderType = (orderType: EOrderType) => {
    switch (orderType) {
      case EOrderType.BUY:
      case EOrderType.SELL:
        return EUIOrderTypeLabel.MARKET;
      case EOrderType.BUY_LIMIT:
      case EOrderType.SELL_LIMIT:
        return EUIOrderTypeLabel.LIMIT;
      case EOrderType.BUY_STOP:
      case EOrderType.SELL_STOP:
        return EUIOrderTypeLabel.STOP;
      case EOrderType.BUY_STOP_LIMIT:
      case EOrderType.SELL_STOP_LIMIT:
        return EUIOrderTypeLabel.STOP_LIMIT;
      case EOrderType.BUY_MARKET_TO_LIMIT:
      case EOrderType.SELL_MARKET_TO_LIMIT:
        return EUIOrderTypeLabel.MARKET_TO_LIMIT;
      default:
        return EUIOrderTypeLabel.MARKET;
    }
  };

  public setFromQuoteboardSymbol = (fromQuoteboardSymbol: boolean) => {
    tradeFormService.setFromQuoteboardSymbol(fromQuoteboardSymbol);
  };

  public clearFromQuoteboardSymbol = () => {
    tradeFormService.setFromQuoteboardSymbol(undefined);
  };

  /**
   * For derivative market or stop order, we need to remove the expiration time and default the Time in force to IOC.
   * @see {@link checkIsDerivativeMarketOrStopOrder} for the check implementation.
   */
  public createOrder = async (param: ICreateOrderActionParams) => {
    const { type: symbolCategory } = param.symbolDetail ?? tradeAnalysisViewController.getSymbolDetail() ?? {};
    const { formData } = param;
    const orderType = formData.orderType ?? this.orderType;
    const isDerivativeMarketOrStopOrder = checkIsDerivativeMarketOrStopOrder(
      symbolCategory,
      orderType !== undefined ? this.mapOrderTypeLabelFromOrderType(orderType) : undefined
    );
    const submittedParams = {
      ...param,
      formData: {
        ...formData,
        fillPolicy: isDerivativeMarketOrStopOrder ? EFillPolicy.IOC : param.formData.fillPolicy,
        expiration: isDerivativeMarketOrStopOrder ? undefined : param.formData.expiration
      }
    };
    if (orderType !== undefined) {
      if (isMarketOrder(orderType)) {
        return this.createMarketTrade(submittedParams);
      } else {
        return this.createLimitTrade(submittedParams);
      }
    }
    return null;
  };

  private createMarketTrade = async ({ formData, symbolDetail, onSuccess, onError }: ICreateOrderActionParams) => {
    const selectedSymbol = symbolDetail?.symbol ?? tradeAnalysisViewController.getSymbolDetail()?.symbol;
    const lotSize = Number(formData.lotSize);
    const orderType = formData.orderType ?? this.orderType;
    const expiration = formData.expiration
      ? getSecondsSinceEpoch(getUTCOffsetDateTimeAsDayjs({ input: formData.expiration }))
      : undefined;

    if (selectedSymbol && lotSize && orderType !== undefined) {
      tradeFormService.setLoading(true);
      tradeAPIController
        .createMarketOrder(
          {
            orderType: orderType as EOrderType.BUY | EOrderType.SELL,
            symbol: selectedSymbol,
            lotSize: lotSize,
            fillPolicy: formData.fillPolicy,
            indicate: EOrderIndicate.PRICE,
            stopLoss: formData.stopLoss ? Number(formData.stopLoss) : undefined,
            takeProfit: formData.takeProfit ? Number(formData.takeProfit) : undefined,
            expiration
          },
          {
            onSuccess: () => {
              onSuccess();
              orderListingViewController.debouncedRefreshOrders({
                origin: 'USER-ACTION',
                refreshOpenOrders: true
              });
            },
            onError: (error) => {
              const { code, message } = error as IApiError;
              const errorMessage = mapResponseCodeToMessage(code, message);
              onError(errorMessage);
            }
          }
        )
        .finally(() => {
          tradeFormService.setLoading(false);
        });
    }
  };

  /* WT-7944 BE Requirement for updating limit orders of tradeExpiry for specified day:
   We must submit the expiration time in server time
   Mobile + Server time: need convert to sever time. When we click on the calender, it takes device time start of day
   Mobile + device time: no need convert to server time, calender does +1/-1 day and gives server time start of day
   Web + Server time: need convert to sever time. When we click on the calender, it takes device time start of day
   Web + device time : need convert to sever time. When we click on the calender, it takes device time start of day */
  private getPendingOrderExpirationTime = (tradeExpiry: EOrderExpiry | undefined, expiration: number | undefined) => {
    const isSpecifiedDay = tradeExpiry === EOrderExpiry.SPECIFIED_DAY;
    const isSpecifiedTimestamp = tradeExpiry === EOrderExpiry.SPECIFIED_TIMESTAMP;

    if (!expiration || (!isSpecifiedDay && !isSpecifiedTimestamp)) {
      return undefined;
    }

    if (isSpecifiedDay) {
      return getSecondsSinceEpoch(
        companyViewController.convertToDayjsWithOffsetServerTime({ input: expiration, keepTime: true })
      );
    }

    return getSecondsSinceEpoch(companyViewController.convertToDayjsWithOffset({ input: expiration, keepTime: true }));
  };

  private createLimitTrade = async ({ formData, symbolDetail, onSuccess, onError }: ICreateOrderActionParams) => {
    const selectedSymbol = symbolDetail?.symbol ?? tradeAnalysisViewController.getSymbolDetail()?.symbol;
    const lotSize = Number(formData.lotSize);
    const orderType = formData.orderType ?? this.orderType;
    const centroidDeviation = !!formData.centroidDeviation ? Number(formData.centroidDeviation) : undefined;
    const expiration = this.getPendingOrderExpirationTime(formData.tradeExpiry, formData.expiration?.getTime());

    if (selectedSymbol && lotSize && orderType !== undefined) {
      tradeFormService.setLoading(true);
      tradeAPIController.createPendingOrder(
        {
          orderType: orderType,
          symbol: selectedSymbol,
          lotSize,
          price: Number(formData.price),
          priceTrigger: Number(formData.priceTrigger),
          fillPolicy: formData.fillPolicy,
          indicate: EOrderIndicate.PRICE,
          stopLoss: formData.stopLoss ? Number(formData.stopLoss) : undefined,
          takeProfit: formData.takeProfit ? Number(formData.takeProfit) : undefined,
          expiration,
          tradeExpiry: formData.tradeExpiry,
          centroidDeviation
        },
        {
          onSuccess: () => {
            onSuccess();
            orderListingViewController.debouncedRefreshOrders({
              origin: 'USER-ACTION',
              refreshOpenOrders: false
            });
          },
          onError: (error) => {
            const { code, message } = error as IApiError;
            const errorMessage = mapResponseCodeToMessage(code, message);
            onError(errorMessage);
          },
          onFinally: () => {
            tradeFormService.setLoading(false);
          }
        }
      );
    }
  };

  public editPendingOrder = async ({
    formData,
    pendingOrder,
    mixpanelProperties,
    onSuccess,
    onError
  }: IEditOrderParams) => {
    const { orderId, symbol, lotSize } = formData;
    const { livePrice } = mixpanelProperties ?? {};
    const { orderType } = pendingOrder ?? {};

    if (!symbol || !orderId || !lotSize || orderType === undefined) {
      return;
    }

    const params: IUpdatePendingOrderParams = this.generateEditPendingOrderParams({ formData, pendingOrder });

    mixpanelAnalysisService.order.editOrderSubmitted({
      livePrice,
      order: pendingOrder,
      values: formData,
      positionModuleTab: EMixpanelPositionModuleTab.PENDING_ORDERS
    });

    tradeFormService.setLoading(true);
    tradeAPIController
      .updatePendingOrder(params, {
        onSuccess: () => {
          onSuccess();

          this.handleEditPendingOrderSuccess({
            formData,
            pendingOrder
          });
        },
        onError: (error) => {
          const { code, message } = error as IApiError;
          const errorMessage = mapResponseCodeToMessage(code, message);
          onError(errorMessage);
        }
      })
      .finally(() => {
        tradeFormService.setLoading(false);
      });
  };

  private generateEditPendingOrderParams = ({
    formData,
    pendingOrder
  }: Pick<IEditOrderParams, 'formData' | 'pendingOrder'>): IUpdatePendingOrderParams => {
    const { orderId, symbol, lotSize, price, priceTrigger, fillPolicy, stopLoss, takeProfit, expiration, tradeExpiry } =
      formData;
    const { id, orderType } = pendingOrder ?? {};
    // For Centroid, use the `id` field value; for others, use `orderId`
    const isCentroid = companyService.getCompanyIntegration() === EMainProductCodeType.CENTROID;
    const orderIdToUpdate = isCentroid && id !== undefined && id !== null ? id.toString() : orderId;

    return {
      symbol: symbol ?? '',
      orderId: orderIdToUpdate ?? '',
      orderType,
      fillPolicy,
      lotSize: Number(lotSize),
      price: price ? Number(price) : undefined,
      priceTrigger: priceTrigger ? Number(priceTrigger) : undefined,
      stopLoss: stopLoss ? Number(stopLoss) : undefined,
      takeProfit: takeProfit ? Number(takeProfit) : undefined,
      expiration: this.getPendingOrderExpirationTime(tradeExpiry, expiration?.getTime()),
      tradeExpiry,
      indicate: EOrderIndicate.PRICE
    };
  };

  private handleEditPendingOrderSuccess = ({
    formData,
    pendingOrder
  }: Pick<IEditOrderParams, 'formData' | 'pendingOrder'>) => {
    const { orderId, price, priceTrigger, stopLoss, takeProfit } = formData;
    if (!orderId) {
      return;
    }

    const { decimal, orderType } = pendingOrder ?? {};

    orderListingViewController.optimisticUpdatePendingOrder(orderId, {
      stopLoss: stopLoss ? Number(stopLoss) : undefined,
      takeProfit: takeProfit ? Number(takeProfit) : undefined,
      price: price ? Number(price) : undefined,
      priceTrigger: priceTrigger ? Number(priceTrigger) : undefined
    });

    orderListingViewController.resetInitialLoading(EColumnPreferenceOrderType.PENDING_ORDER);

    const { entryPrice, additionalPrice } = equityChartViewController.getEntryAndAdditionalPrices({
      orderType,
      price: Number(price),
      priceTrigger: priceTrigger ? Number(priceTrigger) : 0
    });
    const entryPrices = [entryPrice];
    if (additionalPrice !== undefined) {
      entryPrices.push(additionalPrice);
    }

    equityChartViewController.updateTrackOrderPrice({
      orderId,
      decimal: decimal ?? 2,
      stopLoss: stopLoss ? Number(stopLoss) : undefined,
      takeProfit: takeProfit ? Number(takeProfit) : undefined,
      entryPrices
    });
    orderListingViewController.debouncedRefreshOrders({
      origin: 'USER-ACTION',
      refreshOpenOrders: false
    });
  };

  public editMarketOrder = async ({
    formData,
    order,
    mixpanelProperties,
    onSuccess,
    onError
  }: IEditMarketOrderParams) => {
    const { orderId, symbol, lotSize, contractSize, lotSizeStep, fillPolicy, stopLoss, takeProfit } = formData || {};
    const { decimal, copyTradeInfo } = order || {};
    const { livePrice } = mixpanelProperties;

    if (!orderId || !symbol) {
      return;
    }

    mixpanelAnalysisService.order.editOrderSubmitted({
      livePrice,
      order,
      values: formData,
      positionModuleTab: EMixpanelPositionModuleTab.OPEN_POSITIONS
    });

    if (copyTradeInfo) {
      try {
        tradeFormService.setLoading(true);
        await copyTradeAssetViewController.editCopyTradeOrder({
          values: {
            copierId: copyTradeInfo.copierId,
            copiedSignalId: copyTradeInfo.copySignalId,
            stopPrice: stopLoss ? Number(stopLoss) : null,
            limitPrice: takeProfit ? Number(takeProfit) : null
          },
          onSuccess,
          onError: () => onError('')
        });
      } finally {
        tradeFormService.setLoading(false);
      }
      return;
    }

    const units = convertVolumeToUnits(Number(lotSize), contractSize, lotSizeStep);

    const params: IUpdateMarketOrderParams = {
      orderId,
      symbol,
      indicate: EOrderIndicate.PRICE,
      stopLoss: stopLoss ? Number(stopLoss) : undefined,
      takeProfit: takeProfit ? Number(takeProfit) : undefined,
      units: numberOrDefault(units),
      fillPolicy
    };

    tradeFormService.setLoading(true);
    await tradeAPIController.updateMarketOrder(params, {
      onSuccess: () => {
        onSuccess();
        orderListingViewController.resetInitialLoading(EColumnPreferenceOrderType.OPEN_POSITION);

        orderListingViewController.optimisticUpdateOpenPosition(orderId, {
          stopLoss: params.stopLoss,
          takeProfit: params.takeProfit
        });

        // !!! Make sure stopLoss and takeProfit is always price instead of points
        equityChartViewController.updateTrackOrderPrice({
          orderId,
          decimal: decimal ?? 2,
          stopLoss: params.stopLoss,
          takeProfit: params.takeProfit
        });
        orderListingViewController.debouncedRefreshOrders({
          origin: 'USER-ACTION',
          refreshOpenOrders: true
        });
      },
      onError: (error) => {
        const { code, message } = error as IApiError;
        const errorMessage = mapResponseCodeToMessage(code, message);
        onError(errorMessage);
      },
      onFinally: () => {
        tradeFormService.setLoading(false);
      }
    });
  };

  public oneClickCloseMarketOrder = async ({ positionId, onSuccess, onError }: IOneClickCloseOrderActionParams) => {
    const marketOrder = orderListingService.context.mappedOpenPositions[positionId] || ({} as IOrder);
    if (!marketOrder?.symbol) {
      onError?.(i18n.t('No market order found'));
      return;
    }
    const { orderType, lotSize, symbolType: symbolCategory, symbol, orderId } = marketOrder;
    const orderTypeLabel = this.mapOrderTypeLabelFromOrderType(orderType);
    const defaultFillPolicy = this.getFillPolicyDefaultValue({ orderTypeLabel, symbolCategory });
    const lotSizeString = Math.abs(lotSize)?.toString();
    const expiration = marketOrder?.expiration
      ? getUTCOffsetDateTimeAsDayjs({ input: marketOrder.expiration }).toDate()
      : undefined;
    openOrderViewController.closeMarketOrder({
      payload: {
        orderId,
        symbol,
        orderType,
        expiration,
        lotSize: lotSizeString,
        fillPolicy: defaultFillPolicy
      },
      onSuccess,
      onError
    });
  };

  public bulkClosePendingOrder = async ({ params, onSuccess, onError }: IBulkClosePendingOrderFunctionParams) => {
    if (params.length > 0) {
      tradeFormService.setLoading(true);
      tradeAPIController
        .bulkDeletePendingOrder(params, {
          onSuccess: () => {
            onSuccess();
            orderListingViewController.optimisticallyDeletePendingOrdersFromTable(params.map(({ orderId }) => orderId));
            orderListingViewController.debouncedRefreshOrders({
              origin: 'USER-ACTION',
              refreshOpenOrders: false,
              refreshOrderHistory: true
            });
          },
          onError: (error) => {
            const { code, message } = error as IApiError;
            const errorMessage = mapResponseCodeToMessage(code, message);
            onError(errorMessage);
          }
        })
        .finally(() => {
          tradeFormService.setLoading(false);
        });
    }
  };

  public calculateTotalAmount = ({
    lotSize,
    price,
    priceTrigger
  }: {
    lotSize: string;
    price?: string;
    priceTrigger?: string;
  }) => {
    const pricePerLotSize = priceTrigger ?? price;
    if (pricePerLotSize) {
      const pricePerLotSizeBN = new BigNumber(pricePerLotSize);
      const lotSizeBN = new BigNumber(lotSize);
      return pricePerLotSizeBN.times(lotSizeBN).toFixed(2) + ' ' + 'USD';
    }
    return undefined;
  };

  // Wrapper for calculatePointsFromPrice that converts EUIBuySellType to string
  public calculatePointsFromPrice = ({
    price,
    type,
    entryPrice,
    buySell,
    pointStep
  }: {
    price: string;
    type: 'SL' | 'TP';
    entryPrice?: string;
    buySell?: EUIBuySellType;
    pointStep?: number;
  }): string | undefined => {
    if (buySell === undefined || !entryPrice || !pointStep) {
      return undefined;
    }

    const buySellString = buySell === EUIBuySellType.BUY ? 'BUY' : 'SELL';

    return calculatePointsFromPrice({
      price,
      type,
      entryPrice,
      buySell: buySellString,
      pointStep
    });
  };

  // Wrapper for calculatePriceFromPoints that converts EUIBuySellType to string
  public calculatePriceFromPoints = ({
    points,
    type,
    entryPrice,
    buySell,
    pointStep,
    symbolDetail
  }: {
    points: string;
    type: 'SL' | 'TP';
    entryPrice?: string;
    buySell?: EUIBuySellType;
    pointStep?: number;
    symbolDetail?: Partial<ISymbolDetail>;
  }): string | undefined => {
    if (buySell === undefined || !entryPrice || !pointStep) {
      return undefined;
    }

    const buySellString = buySell === EUIBuySellType.BUY ? 'BUY' : 'SELL';

    return calculatePriceFromPoints({
      points,
      type,
      entryPrice,
      buySell: buySellString,
      pointStep,
      symbolDetail
    });
  };

  public getOrderTypeOption = () => {
    const isHorizon = companyService.getCompanyIntegration() === EMainProductCodeType.HORIZON;
    const isMT5 = companyService.getCompanyIntegration() === EMainProductCodeType.METATRADER5;
    const isTenantLirunex = companyService.getCompanyTenantId() === ETenantId.LIRUNEX;
    const isTenantCentroid = companyService.getCompanyTenantId() === ETenantId.CENTROID;

    if (isHorizon) {
      return TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_HORIZON;
    } else if (isMT5 && isTenantCentroid) {
      return TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_DEFAULT;
    } else if (isMT5 && !isTenantLirunex) {
      return TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_MT5;
    }
    return TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_DEFAULT;
  };

  public getOrderTypeDefaultValue = () => {
    return this.getOrderTypeOption()[0];
  };

  public getFillPolicyOption = ({
    orderTypeLabel,
    symbolCategory
  }: {
    orderTypeLabel?: EUIOrderTypeLabel;
    symbolCategory?: ESymbolCategory;
  }) => {
    const isCentroid = companyService.getCompanyIntegration() === EMainProductCodeType.CENTROID;
    const isHorizon = companyService.getCompanyIntegration() === EMainProductCodeType.HORIZON;

    let fillPolicyOption;
    if (isHorizon) {
      fillPolicyOption = CASH_FILL_POLICY_DROPDOWN_LIST_HORIZON;
    } else if (isCentroid) {
      fillPolicyOption = CASH_FILL_POLICY_DROPDOWN_LIST_CENTROID;
    } else if (orderTypeLabel === EUIOrderTypeLabel.MARKET) {
      fillPolicyOption = CASH_FILL_POLICY_DROPDOWN_LIST_MARKET;
    } else {
      fillPolicyOption = CASH_FILL_POLICY_DROPDOWN_LIST_LIMIT;
    }

    const isDerivativeMarketOrStopOrder = checkIsDerivativeMarketOrStopOrder(symbolCategory, orderTypeLabel);

    if (isDerivativeMarketOrStopOrder) {
      fillPolicyOption = derivativeFillPolicyDropDownList;
    }

    return fillPolicyOption;
  };

  public getFillPolicyDefaultValue = ({
    orderTypeLabel,
    symbolCategory
  }: {
    orderTypeLabel?: EUIOrderTypeLabel;
    symbolCategory?: ESymbolCategory;
  }) => {
    const integration = companyService.getCompanyIntegration();
    const isMT4 = integration === EMainProductCodeType.METATRADER4;
    const isMT5 = integration === EMainProductCodeType.METATRADER5;
    const isCentroid = integration === EMainProductCodeType.CENTROID;

    if (isMT4) {
      return undefined;
    }

    if (isMT5) {
      return orderTypeLabel === EUIOrderTypeLabel.MARKET ? EFillPolicy.FOK : EFillPolicy.RETURN;
    }

    if (isCentroid) {
      return orderTypeLabel === EUIOrderTypeLabel.MARKET ? EFillPolicy.IOC : EFillPolicy.GTC;
    }

    const isDerivativeMarketOrStopOrder = checkIsDerivativeMarketOrStopOrder(symbolCategory, orderTypeLabel);
    return isDerivativeMarketOrStopOrder ? EFillPolicy.IOC : EFillPolicy.DAY;
  };

  public getTradeExpiryOption = () => {
    const isMT5 = companyService.getCompanyIntegration() === EMainProductCodeType.METATRADER5;
    return isMT5 ? TRADE_EXPIRY_DROPDOWN_LIST_MT5 : TRADE_EXPIRY_DROPDOWN_LIST;
  };

  public getTradeExpiryDefaultValue = () => {
    return this.getTradeExpiryOption()[0];
  };

  // Method to copy signal data to trade form with point calculations
  public copySignalToForm = (params: {
    entryPrice?: string;
    stopLoss?: string;
    takeProfit?: string;
    buySellType?: EUIBuySellType;
    setValue: (field: keyof import('./types').IUITradeFormData, value: string) => void;
    triggerBlurHandlers?: {
      handleStopLossBlur?: () => void;
      handleTakeProfitBlur?: () => void;
    };
  }) => {
    const { entryPrice, stopLoss, takeProfit, setValue, triggerBlurHandlers } = params;

    // Set entry price first if provided
    if (entryPrice !== undefined) {
      setValue('price', entryPrice);
    }

    // Use a small delay to ensure entry price is set before calculating points
    setTimeout(() => {
      // Set stop loss if provided and trigger blur to calculate points
      if (stopLoss !== undefined) {
        setValue('stopLoss', stopLoss);
        if (triggerBlurHandlers?.handleStopLossBlur) {
          // Small delay to ensure value is set before blur
          setTimeout(() => {
            triggerBlurHandlers.handleStopLossBlur?.();
          }, 5);
        }
      }

      // Set take profit if provided and trigger blur to calculate points
      if (takeProfit !== undefined) {
        setValue('takeProfit', takeProfit);
        if (triggerBlurHandlers?.handleTakeProfitBlur) {
          // Small delay to ensure value is set before blur
          setTimeout(() => {
            triggerBlurHandlers.handleTakeProfitBlur?.();
          }, 5);
        }
      }
    }, 10);
  };
}

export const tradeFormViewController = new TradeFormViewController();
